import createClient, { type Middleware } from 'openapi-fetch';
import type { paths } from './schema';

function cookie(name: string): string | undefined {
  return document.cookie.split('; ').find((c) => c.startsWith(name + '='))?.split('=')[1];
}

const csrf: Middleware = {
  async onRequest({ request }) {
    if (!['GET', 'HEAD', 'OPTIONS'].includes(request.method)) {
      const token = cookie('csrftoken');
      if (token) request.headers.set('X-CSRFToken', token);
    }
    request.headers.set('Accept-Language', localStorage.getItem('locale') ?? 'cs');
    return request;
  },
};

export const api = createClient<paths>({ baseUrl: '/', credentials: 'include' });
api.use(csrf);

/** Ensures the csrftoken cookie exists before the first unsafe request. */
export async function ensureCsrf(): Promise<void> {
  if (!cookie('csrftoken')) await api.GET('/api/auth/csrf');
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    /** The request field the refusal is about, when the backend names one. */
    public field?: string,
  ) {
    super(message);
  }
}

/** A DRF serializer refusal, `{field: [messages]}`, as (field, first message). */
function serializerError(e: unknown): [string, string] | null {
  if (!e || typeof e !== 'object' || Array.isArray(e) || 'error' in e) return null;
  for (const [field, msgs] of Object.entries(e as Record<string, unknown>)) {
    if (Array.isArray(msgs) && typeof msgs[0] === 'string') return [field, msgs[0]];
  }
  return null;
}

/** Unwraps an openapi-fetch result into data or throws ApiError. */
export function unwrap<T>(r: { data?: T; error?: unknown; response: Response }): T {
  if (r.error !== undefined || !r.response.ok) {
    const e = r.error as { error?: { code?: string; message?: string; field?: string } } | undefined;
    const drf = serializerError(r.error);
    if (drf) throw new ApiError(r.response.status, 'http_error', drf[1], drf[0]);
    throw new ApiError(r.response.status, e?.error?.code ?? 'http_error', e?.error?.message ?? r.response.statusText, e?.error?.field);
  }
  return r.data as T;
}
